import type { InvoiceSummary } from '@worki/shared';

export interface ForwardingConfig {
  recipientEmail: string;
  fromDay: string;
  toDay: string;
  delivery: 'pdf' | 'link';
  folderId: string | null;
  accountId: string | null;
  enabled: boolean;
  intervalMinutes: number;
  locale: 'he' | 'en';
}
export interface ForwardingRule extends ForwardingConfig {
  id: string;
  createdAt: string;
  updatedAt: string;
  nextRunAt: string;
  lastRunAt: string | null;
}
export interface ForwardingPreview {
  matchedCount: number;
  pendingCount: number;
  blockedCount: number;
  summary: InvoiceSummary;
  pendingSummary: InvoiceSummary;
}
export interface ForwardingRun {
  id: string;
  ruleId: string;
  status: 'running' | 'completed' | 'failed' | 'uncertain';
  startedAt: string;
  finishedAt: string | null;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  uncertainCount: number;
}
export interface ForwardingDraft {
  recipientEmail: string;
  fromDay: string;
  toDay: string;
  delivery: 'pdf' | 'link';
  folderId: string;
  accountId: string;
  intervalMinutes: string;
}
export const forwardingDraft = (rule?: ForwardingRule): ForwardingDraft => ({
  recipientEmail: rule?.recipientEmail ?? '', fromDay: rule?.fromDay ?? '', toDay: rule?.toDay ?? '',
  delivery: rule?.delivery ?? 'pdf', folderId: rule?.folderId ?? '', accountId: rule?.accountId ?? '',
  intervalMinutes: String(rule?.intervalMinutes ?? 120),
});
export const forwardingWrite = (draft: ForwardingDraft, locale: 'he' | 'en'): ForwardingConfig => ({
  ...draft, recipientEmail: draft.recipientEmail.trim(), folderId: draft.folderId || null,
  accountId: draft.accountId || null, intervalMinutes: Number(draft.intervalMinutes), enabled: false, locale,
});
/** A changed form must be saved and previewed again before it can send. */
export const forwardingReviewMatches = (draft: ForwardingDraft, rule: ForwardingRule): boolean =>
  JSON.stringify(forwardingDraft(rule)) === JSON.stringify({ ...draft, recipientEmail: draft.recipientEmail.trim() });

/** The server compares this snapshot before running a manually reviewed delivery. */
export const forwardingExpectedRule = (rule: ForwardingRule): Omit<ForwardingConfig, 'enabled'> => ({
  recipientEmail: rule.recipientEmail, fromDay: rule.fromDay, toDay: rule.toDay, delivery: rule.delivery,
  folderId: rule.folderId, accountId: rule.accountId, intervalMinutes: rule.intervalMinutes, locale: rule.locale,
});
